/**
 * MonthlyGoalCell.jsx
 * v3.21-1799 · Reusable Monthly Funded Goal card cell.
 *
 * v3.21-1799 changes (P0 fixes on top of v3.21-1798 layout):
 *   • Reporting period (year/month) is supplied by the PARENT from
 *     the same authoritative range that drives the Funded Amount
 *     (finSummary).  No `new Date()` here — the browser calendar is
 *     NEVER the authoritative goal period.
 *   • New `multiMonth` prop → when the parent's reporting range
 *     spans more than one calendar month the cell renders a compact
 *     read-only "Not applicable for multi-month range" state.  Card
 *     dimensions, header, and testid are preserved so the outer
 *     3-card layout footprint never shifts.
 *   • New `canEdit` prop → gates the pencil / Set Goal affordance
 *     on the frontend mirror of the backend `require_admin`
 *     authorization (services/deal_permissions.assert_is_admin).
 *     Unauthorized viewers see a read-only display; no pencil, no
 *     Set Goal, no edit input, no Save.  Backend PUT remains
 *     authoritative and still enforces `require_admin`.
 *
 * Preserved from v3.21-1797/1798:
 *   • Server-persisted via GET/PUT /api/monthly-goal keyed by
 *     (scope, year, month) against db.monthly_funded_goals.
 *   • Never mutates funded_amount / funding_sources / deal_amount /
 *     approved_amount / settlement records — reads `fundedAmount`
 *     from parent SSOT.
 *   • No divide-by-zero, no negative remaining, safe zero-goal
 *     branch.
 */
import { useEffect, useState } from 'react';
import { Pencil, Check, X as XIcon, Target } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';

const fmt$ = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(Number.isFinite(n) ? n : 0);

export default function MonthlyGoalCell({
  scope,           // canonical reporting-scope key (SAME scope as fundedAmount)
  year,            // integer year — derived from the reporting range
  month,           // 1..12       — derived from the reporting range
  fundedAmount,    // current funded total (read-only from parent SSOT)
  multiMonth = false, // v1799 · parent reporting range covers >1 calendar month
  canEdit    = false, // v1799 · frontend mirror of backend require_admin
}) {
  const [goal,       setGoal]       = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [editing,    setEditing]    = useState(false);
  const [draft,      setDraft]      = useState('');
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    // v1799 · Do NOT fetch a single-month goal when the reporting
    // range spans multiple months — there is no single canonical
    // monthly target for a mixed range and we must not aggregate.
    if (multiMonth) {
      setGoal(null);
      setLoading(false);
      return undefined;
    }
    if (!scope || !year || !month) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/monthly-goal', {
          params: { scope, year, month },
        });
        if (!cancelled) setGoal(data?.monthly_funded_goal ?? null);
      } catch (_) {
        if (!cancelled) setGoal(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scope, year, month, multiMonth]);

  // v1799 · If the caller loses authorization mid-flight cancel any
  // pending inline edit so the read-only display re-renders.
  useEffect(() => {
    if (!canEdit && editing) {
      setEditing(false);
      setDraft('');
    }
  }, [canEdit, editing]);

  const startEdit = () => {
    if (!canEdit || multiMonth) return;
    setDraft(goal != null ? String(goal) : '');
    setEditing(true);
  };
  const cancel = () => { setEditing(false); setDraft(''); };
  const save = async () => {
    if (!canEdit || multiMonth) return;
    const val = Number(String(draft).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(val) || val < 0) {
      toast.error('Enter a valid goal amount (0 or more).');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put('/monthly-goal', {
        scope, year, month,
        monthly_funded_goal: val,
      });
      setGoal(data?.monthly_funded_goal ?? val);
      setEditing(false);
      toast.success('Monthly goal saved.');
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        toast.error('You do not have permission to change the monthly goal.');
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to save goal.');
      }
    } finally { setSaving(false); }
  };

  // Derived metrics — no divide-by-zero, no negative remaining.
  const funded = Number(fundedAmount || 0);
  const g = Number.isFinite(goal) && goal > 0 ? Number(goal) : 0;
  const exceeded = g > 0 && funded > g;
  const remaining = g > 0 ? Math.max(g - funded, 0) : 0;
  const pctRaw = g > 0 ? (funded / g) * 100 : 0;
  const pct = Math.round(pctRaw * 10) / 10;

  const showEditAffordance = canEdit && !multiMonth && !editing;

  return (
    <div className="w-full" data-testid="monthly-goal-cell">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide flex items-center gap-1">
          <Target className="h-3 w-3" /> Monthly Goal
        </p>
        {showEditAffordance && (
          <button
            type="button"
            onClick={startEdit}
            className="text-slate-400 hover:text-blue-600"
            title="Edit monthly goal"
            data-testid="monthly-goal-edit-btn"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {multiMonth ? (
        /* v1799 · Multi-month reporting range — no single-month goal
           is applicable.  Preserve card footprint by rendering a
           compact read-only note in place of the goal metrics. */
        <>
          <p
            className="text-lg font-bold text-slate-400 mt-0.5"
            data-testid="monthly-goal-multi-month"
          >—</p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            Not applicable for multi-month range
          </p>
        </>
      ) : editing ? (
        <div className="mt-1 flex items-center gap-1" data-testid="monthly-goal-editor">
          <span className="text-sm text-slate-500">$</span>
          <Input
            type="text"
            inputMode="decimal"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="h-7 text-sm px-1.5"
            placeholder="0"
            autoFocus
            data-testid="monthly-goal-input"
          />
          <Button
            size="sm"
            className="h-7 px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={saving}
            onClick={save}
            data-testid="monthly-goal-save"
          ><Check className="h-3 w-3" /></Button>
          <Button
            size="sm" variant="outline"
            className="h-7 px-2"
            disabled={saving}
            onClick={cancel}
            data-testid="monthly-goal-cancel"
          ><XIcon className="h-3 w-3" /></Button>
        </div>
      ) : g > 0 ? (
        <>
          <p className="text-lg font-bold text-slate-800 mt-0.5" data-testid="monthly-goal-value">
            {fmt$(g)}
          </p>
          {exceeded ? (
            <>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mt-1">Goal Exceeded By</p>
              <p className="text-sm font-semibold text-emerald-700" data-testid="monthly-goal-exceeded">
                {fmt$(funded - g)}
              </p>
            </>
          ) : (
            <>
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mt-1">Remaining</p>
              <p className="text-sm font-semibold text-slate-800" data-testid="monthly-goal-remaining">
                {fmt$(remaining)}
              </p>
            </>
          )}
          <p className="text-[10px] text-slate-500 mt-0.5" data-testid="monthly-goal-pct">
            {pct.toFixed(1)}% of goal
          </p>
        </>
      ) : (
        <>
          <p className="text-lg font-bold text-slate-400 mt-0.5" data-testid="monthly-goal-empty">—</p>
          {canEdit && (
            <button
              type="button"
              onClick={startEdit}
              className="mt-1 text-xs text-blue-600 hover:underline"
              data-testid="monthly-goal-set-btn"
            >Set Goal</button>
          )}
        </>
      )}
      {loading && (
        <p className="text-[10px] text-slate-300 italic mt-1">Loading…</p>
      )}
    </div>
  );
}
